/**
 * ErrorFallback - User-friendly fallback UI for error boundaries.
 * WHY: Prevents white-screen crashes by showing a recovery UI
 *      when a rendering error occurs in any route component.
 */
import type { FallbackProps } from 'react-error-boundary';
import { AlertCircle, RotateCcw } from 'lucide-react';
import { Button, Card } from '@verif-y/ui';

export function ErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  const errorMessage = error instanceof Error ? error.message : String(error);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <Card variant="glass" className="max-w-md w-full p-8 text-center space-y-4">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-100 mx-auto">
          <AlertCircle size={28} className="text-red-600" aria-hidden="true" />
        </div>
        <div role="alert">
          <h2 className="text-xl font-semibold text-foreground">Something went wrong</h2>
          <p className="text-sm text-muted-foreground mt-2">
            We're sorry for the inconvenience. Please try again or navigate to another page.
          </p>
          {import.meta.env.DEV && errorMessage && (
            <pre className="mt-3 p-3 bg-muted rounded-lg text-xs text-left text-muted-foreground overflow-auto max-h-32">
              {errorMessage}
            </pre>
          )}
        </div>
        <div className="flex gap-3 justify-center pt-2">
          <Button onClick={resetErrorBoundary}>
            <RotateCcw size={14} aria-hidden="true" /> Try Again
          </Button>
          <Button variant="outline" onClick={() => { window.location.href = '/'; }}>
            Go Home
          </Button>
        </div>
      </Card>
    </div>
  );
}
